'use client';

import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useRouter,
} from 'next/navigation';
import {
  useAuth,
} from '@/contexts/AuthContext';
import {
  ReactNode,
} from 'react';
import {
  clearStoredAuth,
} from '@/utils/auth';
import FullScreenSpinner from '@/core/components/FullScreenSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const [forceRedirecting, setForceRedirecting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const timer = setTimeout(() => {
        if (typeof window !== 'undefined') {
          clearStoredAuth();
        }
        router.replace('/authorize');
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading, router]);

  // Safety: if we remain in loading state too long while unauthenticated, push to /authorize
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (isLoading && !isAuthenticated) {
      timeoutRef.current = setTimeout(() => {
        setForceRedirecting(true);
        router.replace('/authorize');
      }, 2000);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, isAuthenticated, router]);

  if (isLoading && !isAuthenticated) {
    return <FullScreenSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-t-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}